import { Autocomplete, Stack, TextField } from '@mui/material';
import { GridFilterInputValueProps, GridFilterOperator, getGridStringOperators } from '@mui/x-data-grid-pro';
import type { CampaignLeaksT } from './i18n';

/** Text filter: "contains" only. */
export const containsOperators = (): GridFilterOperator[] =>
  getGridStringOperators().filter((operator) => operator.value === 'contains');

/** Input of "is any of", declared once so the grid keeps it mounted (and the list open) while typing. */
function AnyOfInput({
  item,
  applyValue,
  options,
  placeholder,
}: GridFilterInputValueProps & { options: string[]; placeholder: string }) {
  return (
    <Autocomplete
      sx={{ mt: 2.5, minWidth: 220 }}
      multiple
      options={options}
      value={item.value || []}
      onChange={(_event, newValue) => applyValue({ ...item, value: newValue })}
      renderInput={(params) => <TextField {...params} variant="standard" placeholder={placeholder} />}
      size="small"
    />
  );
}

/**
 * Multiple choice (searchable): "is any of", with the Autocomplete of the Brand Shield grids' filters.
 * The filtering itself is done by applyFilters.
 */
export const anyOfOperators = (ct: CampaignLeaksT, options: string[], placeholder = ct('filters.select_values')): GridFilterOperator[] => [
  {
    label: ct('filters.is_any_of'),
    value: 'isAnyOf',
    getApplyFilterFn: () => null,
    InputComponent: AnyOfInput as GridFilterOperator['InputComponent'],
    InputComponentProps: { options, placeholder },
  },
];

function RangeInput({ item, applyValue, labels }: GridFilterInputValueProps & { labels: { min: string; max: string } }) {
  const [min, max] = Array.isArray(item.value) ? item.value : [null, null];
  const update = (index: 0 | 1, raw: string) => {
    const next = [min, max];
    next[index] = raw === '' ? null : Number(raw);
    applyValue({ ...item, value: next });
  };
  return (
    <Stack direction="row" spacing={1} sx={{ pl: 1 }}>
      <TextField
        label={labels.min}
        type="number"
        variant="standard"
        value={min ?? ''}
        onChange={(event) => update(0, event.target.value)}
        sx={{ width: 90 }}
      />
      <TextField
        label={labels.max}
        type="number"
        variant="standard"
        value={max ?? ''}
        onChange={(event) => update(1, event.target.value)}
        sx={{ width: 90 }}
      />
    </Stack>
  );
}

/** Numeric min / max range. The filtering itself is done by applyFilters. */
export const rangeOperators = (ct: CampaignLeaksT): GridFilterOperator[] => [
  {
    label: ct('filters.between'),
    value: 'between',
    getApplyFilterFn: () => null,
    InputComponent: RangeInput as GridFilterOperator['InputComponent'],
    InputComponentProps: { labels: { min: ct('filters.min'), max: ct('filters.max') } },
  },
];
